import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';
import { usePress, useSwitch, useFocusRing, VisuallyHidden, mergeProps } from 'react-aria';
import { useToggleState } from 'react-stately';

import { RouteManager } from '../../RouteManager';
import { ToastManager } from '../../ToastManager';

import { useI18n } from '../../hooks/useI18nFormatters';
import { useIsRTL } from '../../hooks/useIsRTL';
import { useApi } from '../../store/SelectedHomeyStore.hooks.ts';
import { useUserMe } from '../../store/users/useUserMe.ts';
import { useUser } from '../../store/users/useUsers.ts';

import { publicUrl } from '../../theme/functions/publicUrl.js';
import { su } from '../../theme/functions/su';
import { glyph } from '../../theme/glyph';
import { theme } from '../../theme/theme';

import { IconButton } from '../../components/buttons/IconButton';
import { SolidButton } from '../../components/buttons/SolidButton';
import { Glyph as GlyphBase } from '../../components/common/Glyph';
import { Separator } from '../../components/common/Separator';
import { SpinnerFade } from '../../components/common/SpinnerFade';
import { Heading3Medium } from '../../components/common/text/Heading3Medium';
import { Dialog } from '../../components/overlay/Dialog';

export function PresenceDialog(props) {
  const { i18n } = useI18n();
  const { api } = useApi();

  const { userMe } = useUserMe();
  const { user } = useUser({ userId: props.userId });

  const userId = user?.id ?? null;
  const userPresent = user?.present ?? true;
  const userAsleep = user?.asleep ?? true;

  const [state, setState] = useState({
    userPresent,
    userAsleep,
    userId,
  });

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setState({
      userPresent,
      userAsleep,
      userId,
    });
  }, [userId, userPresent, userAsleep]);

  let handler = {
    isOpen: true,
    close() {
      RouteManager.toOmitQuery();
    },
  };

  return (
    handler.isOpen && (
      <Dialog onClose={() => handler.close()} isOpen={handler.isOpen}>
        <S.Content>
          <S.TopCircle />
          <S.Avatar src={user?.avatar || publicUrl(`img/ph-64.png`)} />

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              padding: su(2),
            }}
          >
            <IconButton
              url={glyph.core.cross}
              color={theme.color.icon_light}
              size={theme.icon.size_default}
              onPress={() => handler.close()}
            />
          </div>

          <S.Spacer
            style={{
              paddingTop: 16,
            }}
          >
            <Heading3Medium>{user?.name.split(' ')[0]}</Heading3Medium>
          </S.Spacer>

          <S.SpacerColumn>
            <Switch
              activeText={i18n.messageFormatter('presence.present.true')}
              activeIcon={glyph.system.home}
              inActiveText={i18n.messageFormatter('presence.present.false')}
              inActiveIcon={glyph.core.doorArrowRight}
              isActive={state.userPresent}
              onChange={(isActive) => {
                setState((prevState) => {
                  return {
                    ...prevState,
                    userPresent: isActive,
                  };
                });
              }}
            />
            <Switch
              activeText={i18n.messageFormatter('presence.asleep.false')}
              activeIcon={glyph.core.sun}
              inActiveText={i18n.messageFormatter('presence.asleep.true')}
              inActiveIcon={glyph.core.moon}
              isActive={state.userAsleep === false}
              onChange={(isActive) => {
                setState((prevState) => {
                  return {
                    ...prevState,
                    userAsleep: !isActive,
                  };
                });
              }}
            />
          </S.SpacerColumn>

          <Separator />

          <S.Spacer>
            <SolidButton
              styleFlat={true}
              styleColor="green"
              styleWidth="full"
              isDisabled={isSaving === true}
              onPress={() => {
                (async () => {
                  setIsSaving(true);

                  try {
                    if (user.id === userMe.id) {
                      await api.presence.setPresentMe({
                        value: state.userPresent,
                      });
                      await api.presence.setAsleepMe({
                        value: state.userAsleep,
                      });
                    } else {
                      await api.presence.setPresent({
                        id: user.id,
                        value: state.userPresent,
                      });
                      await api.presence.setAsleep({
                        id: user.id,
                        value: state.userAsleep,
                      });
                    }
                  } catch (error) {
                    ToastManager.handleError(error);
                  } finally {
                    setTimeout(() => {
                      setIsSaving(false);
                      handler.close();
                    }, 0);
                  }
                })();
              }}
            >
              <SpinnerFade
                isActive={isSaving === true}
                size={theme.icon.size_small}
                color={theme.color.white}
              >
                <SolidButton.Icon
                  source={glyph.core.checkmark}
                  color={theme.color.white}
                  size={theme.icon.size_default}
                />
              </SpinnerFade>
              <SolidButton.Text>
                {/* {isSaving === true
                  ? `${i18n.messageFormatter('common.saving')}...`
                  : i18n.messageFormatter('common.save')} */}
                {i18n.messageFormatter('common.save')}
              </SolidButton.Text>
            </SolidButton>
          </S.Spacer>
        </S.Content>
      </Dialog>
    )
  );
}

function S() {}
PresenceDialog.S = S;

S.Content = styled.div`
  position: relative;
  min-width: 314px;

  ${SolidButton.S.Root} {
    flex: 1 1 auto;
  }
`;

S.Spacer = styled.div`
  display: flex;
  justify-content: center;
  padding: ${su(1)};
`;

S.SpacerColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: ${su(2)} 0 ${su(3)};

  & > :first-of-type {
    margin-bottom: ${su(2)};
  }
`;

S.TopCircle = styled.div`
  position: absolute;
  top: -54px;
  right: 50%;
  transform: translateX(50%);
  border-radius: 50%;
  background-color: ${theme.color.component};
  width: 108px;
  height: 108px;
  box-shadow: ${theme.boxShadow.default};
  clip-path: inset(0 0 49% 0);
`;

S.Avatar = styled.img`
  position: absolute;
  top: ${su(-6)};
  right: 50%;
  transform: translateX(50%);
  border-radius: 50%;
  width: 100px;
  height: 100px;
  z-index: 1;
`;

const variants = {
  activeText: {
    color: theme.color.text,
  },
  activeIcon: {
    color: theme.icon.color_dark,
  },
  inActiveText: {
    color: theme.color.text_light,
  },
  inActiveIcon: {
    color: theme.icon.color_light,
  },
};

function Switch({ isActive, onChange, activeText, inActiveText, activeIcon, inActiveIcon }) {
  const toggleState = useToggleState({
    isSelected: isActive,
  });
  const press = usePress({
    onPress() {
      onChange(!isActive);
    },
  });
  const isRTL = useIsRTL();
  const inputRef = useRef();
  const aSwitch = useSwitch({ 'aria-label': 'Switch' }, toggleState, inputRef);
  const focus = useFocusRing();

  return (
    <SwitchContainer {...press.pressProps} isFocusVisible={focus.isFocusVisible}>
      <VisuallyHidden>
        <input {...mergeProps(aSwitch.inputProps, focus.focusProps)} ref={inputRef} />
      </VisuallyHidden>
      <Handle
        initial={false}
        animate={{
          x: toggleState.isSelected ? '0%' : isRTL ? '-100%' : '100%',
        }}
      />
      <TextContainer>
        <Text
          initial={false}
          animate={toggleState.isSelected === true ? 'activeText' : 'inActiveText'}
          variants={variants}
        >
          <IconWrapper
            initial={false}
            animate={toggleState.isSelected === true ? 'activeIcon' : 'inActiveIcon'}
            variants={variants}
          >
            <GlyphBase source={activeIcon} size="14px" color="currentColor" />
          </IconWrapper>
          {activeText}
        </Text>
        <Text
          initial={false}
          animate={toggleState.isSelected === false ? 'activeText' : 'inActiveText'}
          variants={variants}
        >
          <IconWrapper
            initial={false}
            animate={toggleState.isSelected === false ? 'activeIcon' : 'inActiveIcon'}
            variants={variants}
          >
            <GlyphBase source={inActiveIcon} size="14px" color="currentColor" />
          </IconWrapper>
          {inActiveText}
        </Text>
      </TextContainer>
    </SwitchContainer>
  );
}

const SwitchContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 220px;
  height: 34px;
  border-radius: 17px;
  background-color: ${theme.color.body};
  cursor: pointer;
`;

const IconWrapper = styled(motion.span)`
  display: flex;
  margin-inline-start: 14px;
  margin-inline-end: 6px;
`;

const TextContainer = styled.div`
  position: absolute;
  display: flex;
  background-color: transparent;
  width: 220px;
  height: 34px;
  border-radius: 17px;
`;

const Text = styled(motion.div)`
  display: flex;
  align-items: center;
  flex: 1 1 50%;
  height: 34px;
  line-height: 34px;
  vertical-align: center;
  font-size: ${theme.fontSize.small};
`;

const Handle = styled(motion.div)`
  display: flex;
  width: 110px;
  height: 34px;
  border-radius: 17px;
  background-color: ${theme.color.component};
  box-shadow: ${theme.boxShadow.default};
`;
